import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { analyzeInsight, getInsightDashboard } from '../../utils/insightApi';
import { hairAppPath, insightDashboardPath, resolveInsightSessionId, setHairInsightSessionId } from '../../config/hairApp';
import './HairInsight.css';

const STEPS = [
  'Reviewing hair coverage',
  'Analyzing scalp close-ups',
  'Preparing your Hair Insight report',
];

const STEP_DELAYS_MS = [0, 2800, 7000];

export default function HairInsightAnalyzing() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = resolveInsightSessionId(location, null);
  const partialAnalysis = Boolean(location.state?.partialAnalysis);
  const flowToken = location.state?.token || null;
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlHeight = html.style.height;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      html.style.height = prevHtmlHeight;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      navigate(hairAppPath('/insight/capture'), { replace: true });
      return undefined;
    }

    finishedRef.current = false;

    const stepTimers = STEP_DELAYS_MS.slice(1).map((delayMs, index) =>
      window.setTimeout(() => {
        if (!finishedRef.current) {
          setStep(index + 1);
        }
      }, delayMs),
    );

    let cancelled = false;

    const goToDashboard = (dashboardPayload) => {
      if (cancelled || finishedRef.current || !sessionId) return;
      finishedRef.current = true;
      setStep(STEPS.length - 1);
      setHairInsightSessionId(sessionId);
      navigate(insightDashboardPath(sessionId), {
        state: {
          insightSessionId: sessionId,
          dashboard: dashboardPayload || undefined,
          token: flowToken,
        },
        replace: true,
      });
    };

    (async () => {
      try {
        const res = await analyzeInsight(sessionId, { partialAnalysis });
        if (cancelled) return;
        goToDashboard(res?.dashboard || res?.analysis);
      } catch (e) {
        if (cancelled) return;
        if (e?.name === 'TimeoutError') {
          try {
            const recovered = await getInsightDashboard(sessionId);
            const dashboard = recovered?.dashboard || recovered?.analysis;
            if (dashboard) {
              goToDashboard(dashboard);
              return;
            }
          } catch {
            /* fall through to error UI */
          }
        }
        setError(e.message || 'Analysis is temporarily unavailable. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
      stepTimers.forEach((id) => window.clearTimeout(id));
    };
  }, [sessionId, navigate, partialAnalysis, flowToken]);

  return (
    <div className="hair-insight-page page-bg hi-scroll-page hi-analyzing-page">
      <header className="hi-header hi-analyzing-page__header">
        <div>
          <p className="hi-eyebrow">Hair Insight</p>
          <h1>Checking your hair</h1>
        </div>
        <div className="hi-progress-steps">
          <span className="is-done">1. Hair Scan</span>
          <span className="is-done">2. Scalp Detail</span>
          <span className="is-on">3. Results</span>
        </div>
      </header>

      <div className="hi-analyzing-stage" role="status" aria-live="polite">
        <div className="hi-analyzing-card">
          <div className="hi-spinner" aria-hidden />
          {!error ? (
            <>
              <p className="hi-analyzing-step">{STEPS[step]}</p>
              <p className="hi-muted">This usually takes under a minute. Please keep this screen open.</p>
            </>
          ) : (
            <>
              <p className="hi-error">{error}</p>
              <button
                type="button"
                className="hi-btn primary"
                onClick={() =>
                  navigate(hairAppPath('/insight/microscope'), {
                    state: location.state,
                  })
                }
              >
                Go back
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
